import React from 'react';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';

interface PaginationNavProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  itemLabel?: string;
  className?: string;
}

export const PaginationNav: React.FC<PaginationNavProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [6, 12, 24, 48],
  itemLabel = 'item',
  className = '',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isDropdownOpen]);

  if (totalItems === 0 || totalPages <= 1 && !onItemsPerPageChange) {
    if (totalItems === 0) return null;
  }

  const startItem = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers with ellipses
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('..');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-200/80 ${className}`}>
      {/* Information & Items Per Page Selector */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 w-full sm:w-auto justify-between sm:justify-start">
        <div>
          Menampilkan <span className="font-bold text-slate-900">{startItem} - {endItem}</span> dari{' '}
          <span className="font-bold text-slate-900">{totalItems}</span> {itemLabel}
        </div>

        {onItemsPerPageChange && (
          <div className="relative flex items-center" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 text-slate-600 hover:border-slate-300 transition cursor-pointer shadow-3xs min-h-[44px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-500" />
              <span className="text-[11px] font-medium text-slate-500">Tampilkan:</span>
              <span className="font-bold text-slate-800 text-xs">{itemsPerPage}</span>
              <span className="text-[8px] text-slate-400">▼</span>
            </button>

            {isDropdownOpen && (
              <div className="absolute bottom-full left-0 mb-1.5 z-30 min-w-[100px] bg-white border border-slate-200 rounded-xl shadow-lg p-1 space-y-0.5 animate-in fade-in slide-in-from-bottom-1 duration-150">
                {itemsPerPageOptions.map((option) => {
                  const isSelected = option === itemsPerPage;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        onItemsPerPageChange(option);
                        onPageChange(1);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition min-h-[44px] flex items-center ${
                        isSelected
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          {/* Previous Button */}
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Halaman Sebelumnya"
            className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-slate-700 disabled:hover:border-slate-200 transition cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-1 shadow-2xs min-h-[44px] min-w-[44px]"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden xs:inline">Sebelumnya</span>
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {pages.map((p, idx) => {
              if (typeof p === 'string') {
                return (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-xs text-slate-400 font-bold select-none min-h-[44px] min-w-[32px] flex items-center justify-center">
                    ...
                  </span>
                );
              }

              const isCurrent = p === currentPage;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  aria-label={`Halaman ${p}`}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={`min-w-[44px] min-h-[44px] rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center ${
                    isCurrent
                      ? 'bg-indigo-600 text-white shadow-xs scale-105'
                      : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-indigo-600 border border-slate-200/80 shadow-2xs'
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Halaman Selanjutnya"
            className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-slate-700 disabled:hover:border-slate-200 transition cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-1 shadow-2xs min-h-[44px] min-w-[44px]"
          >
            <span className="hidden xs:inline">Selanjutnya</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
